import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { getIngredient, deleteIngredient } from '../graphql/ingredients';
import type { Ingredient } from '../types/ingredient';

const client = generateClient();

interface IngredientResponse {
  userId: string;
  ingredientId: string;
  name: string;
  brand?: string | null;
  strain?: string | null;
  varietal?: string | null;
  thc?: number | null;
  thca?: number | null;
  cbd?: number | null;
  cbda?: number | null;
  cbg?: number | null;
  cbn?: number | null;
  createdAt: string;
  updatedAt: string;
}

function mapResponseToIngredient(data: IngredientResponse): Ingredient {
  return {
    userId: data.userId,
    ingredientId: data.ingredientId,
    name: data.name,
    brand: data.brand ?? undefined,
    strain: data.strain ?? undefined,
    varietal: (data.varietal as Ingredient['varietal']) ?? undefined,
    cannabinoids: {
      thc: data.thc ?? undefined,
      thca: data.thca ?? undefined,
      cbd: data.cbd ?? undefined,
      cbda: data.cbda ?? undefined,
      cbg: data.cbg ?? undefined,
      cbn: data.cbn ?? undefined,
    },
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export function IngredientDetailPage() {
  const { ingredientId } = useParams<{ ingredientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ingredient, setIngredient] = useState<Ingredient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchIngredient = useCallback(async () => {
    if (!user?.userId || !ingredientId) return;

    setLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const res = await client.graphql({
        query: getIngredient,
        variables: { userId: user.userId, ingredientId },
      });

      const data = (res as { data: { getIngredient: IngredientResponse | null } }).data.getIngredient;

      if (!data) {
        setNotFound(true);
      } else {
        setIngredient(mapResponseToIngredient(data));
      }
    } catch {
      setError('Failed to load ingredient. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.userId, ingredientId]);

  useEffect(() => {
    fetchIngredient();
  }, [fetchIngredient]);

  const handleDelete = async () => {
    if (!user?.userId || !ingredientId) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await client.graphql({
        query: deleteIngredient,
        variables: { input: { userId: user.userId, ingredientId } },
      });
      navigate('/ingredients');
    } catch {
      setDeleteError('Failed to delete ingredient. Please try again.');
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <LoadingSpinner />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="page">
        <p>Ingredient not found. It may have been deleted or does not exist.</p>
        <Link to="/ingredients" className="cta-button" style={{ marginTop: '1rem', display: 'inline-block' }}>
          Back to Ingredients
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <ErrorMessage message={error} onRetry={fetchIngredient} />
      </div>
    );
  }

  if (!ingredient) return null;

  const cannabinoidEntries = Object.entries(ingredient.cannabinoids)
    .filter(([, value]) => value != null)
    .map(([key, value]) => ({ label: key.toUpperCase(), value: value as number }));

  return (
    <div className="page ingredient-detail-page">
      <div className="ingredient-detail-header">
        <h2>{ingredient.name}</h2>
        <div className="ingredient-detail-actions">
          <button
            className="cta-button"
            onClick={() => navigate(`/ingredients/${ingredientId}/edit`)}
          >
            Edit
          </button>
          <button
            className="cta-button cta-button-danger"
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete
          </button>
        </div>
      </div>

      {(ingredient.brand || ingredient.strain || ingredient.varietal) && (
        <section className="ingredient-detail-section">
          <h3>Details</h3>
          <div className="ingredient-detail-meta">
            {ingredient.brand && (
              <dl className="ingredient-detail-field">
                <dt>Brand</dt>
                <dd>{ingredient.brand}</dd>
              </dl>
            )}
            {ingredient.strain && (
              <dl className="ingredient-detail-field">
                <dt>Strain</dt>
                <dd>{ingredient.strain}</dd>
              </dl>
            )}
            {ingredient.varietal && (
              <dl className="ingredient-detail-field">
                <dt>Varietal</dt>
                <dd>{ingredient.varietal}</dd>
              </dl>
            )}
          </div>
        </section>
      )}

      {cannabinoidEntries.length > 0 && (
        <section className="ingredient-detail-section">
          <h3>Cannabinoid Profile</h3>
          <div className="ingredient-cannabinoid-grid">
            {cannabinoidEntries.map(({ label, value }) => (
              <div key={label} className="ingredient-cannabinoid-item">
                <span className="ingredient-cannabinoid-label">{label}</span>
                <span className="ingredient-cannabinoid-value">{value.toFixed(2)}%</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {deleteError && (
        <div className="ingredient-detail-error">
          <ErrorMessage message={deleteError} />
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Ingredient"
        message={`Are you sure you want to delete "${ingredient.name}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {deleting && <LoadingSpinner />}
    </div>
  );
}
